import { IFeedResponse, IFeedResponseV4 } from "src/api/client/feed/feed.api.types";
import { feedKeys } from "src/api/client/feed/feed.query-keys";
import { EnumFeedType } from "src/api/server/feed.types";
import { HttpMethod } from "src/constants/http-method";
import { useInfiniteQuery } from "src/lib/tanstack-query/hooks/use-infinite-query";

export interface IFeedInfiniteQueryParams {
    feedUrl: string;
    enabled: boolean;
    sessionId?: string;
    feedType?: EnumFeedType;
    wikifolioId?: string;
}

const LIMIT = 6;
/**
 * @deprecated Use {@link useFeedInfiniteQueryV4} instead. This targets the V3 feed API,
 * which is being replaced by the V4 SmartFeed endpoints.
 */
export const useFeedInfiniteQuery = ({ feedUrl, sessionId, feedType, wikifolioId, enabled }: IFeedInfiniteQueryParams) => {
    const queryParams = new URLSearchParams();
    if (sessionId) {
        queryParams.append("sessionId", sessionId);
    }

    if (feedType) {
        queryParams.append("feedType", feedType.toString());
    }

    if (wikifolioId) {
        queryParams.append("wikifolioId", wikifolioId);
    }

    return useInfiniteQuery<IFeedResponse>(
        feedUrl,
        {
            getNextPageParam: (lastPage: IFeedResponse) => {
                return lastPage.data.next ? lastPage.data.pageNumber + 1 : undefined;
            },
            queryParams,
            refetchOnWindowFocus: false,
            enabled,
        },
        undefined,
        feedType ? feedKeys.user(feedType) : undefined
    );
};

export interface IFeedInfiniteQueryV4Params {
    feedUrl: string;
    feedName: string;
    enabled: boolean;
    feedType?: EnumFeedType;
    userId?: string;
    underlyingIsins?: string[];
    wikifolioIds?: string[];
}

export const useFeedInfiniteQueryV4 = ({
    feedUrl,
    feedName,
    feedType,
    enabled,
    userId,
    underlyingIsins,
    wikifolioIds,
}: IFeedInfiniteQueryV4Params) => {
    return useInfiniteQuery<IFeedResponseV4>(
        feedUrl,
        {
            getNextPageParam: (lastPage: IFeedResponseV4, allPages: IFeedResponseV4[]) => {
                if (lastPage?.links?.next) {
                    return allPages.length + 1; // Next page number
                }
                return undefined;
            },
            method: HttpMethod.POST,
            body: (pageParam: number) => {
                const offset = (pageParam - 1) * LIMIT;
                return JSON.stringify({
                    feedName,
                    ...(userId && { userId }),
                    underlyingIsins,
                    wikifolioIds,
                    offset,
                    limit: LIMIT,
                });
            },
            refetchOnWindowFocus: false,
            enabled,
        },
        undefined,
        feedKeys.v4(feedName, feedType, userId, underlyingIsins, wikifolioIds)
    );
};
